import Vue from 'vue'
import Vuex from 'vuex'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    // 用户登录token
    token: localStorage.getItem('token') || null,
    // 学习进度
    learningProgress: {
      currentChapter: 1,
      currentLesson: 1,
      completedLessons: []
    },
    // 当前选中的课程类型和视频
    currentCourseType: null,
    currentVideoId: null,
    // 课程数据
    courseData: {
      // 课程类型数据，与Notice.vue中的数据结构保持一致
      courseTypes: {},
      // 保留原有的章节结构用于兼容
      chapters: []
    }
  },
  mutations: {
    setCurrentChapter(state, chapterId) {
      state.learningProgress.currentChapter = chapterId
    },
    setCurrentLesson(state, lessonId) {
      state.learningProgress.currentLesson = lessonId
    },
    completeLesson(state, lessonId) {
      if (!state.learningProgress.completedLessons.includes(lessonId)) {
        state.learningProgress.completedLessons.push(lessonId)
      }
    },
    setCurrentCourseType(state, courseType) {
      state.currentCourseType = courseType
    },
    setCurrentVideoId(state, videoId) {
      state.currentVideoId = videoId
    },
    // 移除completeVideo和resetVideoProgress方法，现在使用API解锁机制
    setToken(state, token) {
      state.token = token
      localStorage.setItem('token', token)
    },
    updateCourseVideos(state, { courseType, videos }) {
      console.log('updateCourseVideos called:', { courseType, videos });
      if (!state.courseData.courseTypes[courseType]) {
        state.courseData.courseTypes[courseType] = {};
      }
      state.courseData.courseTypes[courseType].videos = videos;
      console.log('updateCourseVideos completed. courseTypes:', state.courseData.courseTypes);
    },
    updateCourseType(state, { courseType, name }) {
      console.log('updateCourseType called:', { courseType, name });
      if (!state.courseData.courseTypes[courseType]) {
        state.courseData.courseTypes[courseType] = {};
      }
      state.courseData.courseTypes[courseType].name = name;
      console.log('updateCourseType completed. courseTypes:', state.courseData.courseTypes);
    }
  },
  actions: {
    nextLesson({ commit, state }) {
      const currentChapter = state.courseData.chapters.find(
        ch => ch.id === state.learningProgress.currentChapter
      )
      const currentLessonIndex = currentChapter.lessons.findIndex(
        lesson => lesson.id === state.learningProgress.currentLesson
      )
      
      if (currentLessonIndex < currentChapter.lessons.length - 1) {
        // 下一课时
        commit('setCurrentLesson', currentChapter.lessons[currentLessonIndex + 1].id)
      } else {
        // 下一章节
        const currentChapterIndex = state.courseData.chapters.findIndex(
          ch => ch.id === state.learningProgress.currentChapter
        )
        if (currentChapterIndex < state.courseData.chapters.length - 1) {
          const nextChapter = state.courseData.chapters[currentChapterIndex + 1]
          commit('setCurrentChapter', nextChapter.id)
          commit('setCurrentLesson', nextChapter.lessons[0].id)
        }
      }
    },
    previousLesson({ commit, state }) {
      const currentChapter = state.courseData.chapters.find(
        ch => ch.id === state.learningProgress.currentChapter
      )
      const currentLessonIndex = currentChapter.lessons.findIndex(
        lesson => lesson.id === state.learningProgress.currentLesson
      )
      
      if (currentLessonIndex > 0) {
        // 上一课时
        commit('setCurrentLesson', currentChapter.lessons[currentLessonIndex - 1].id)
      } else {
        // 上一章节
        const currentChapterIndex = state.courseData.chapters.findIndex(
          ch => ch.id === state.learningProgress.currentChapter
        )
        if (currentChapterIndex > 0) {
          const prevChapter = state.courseData.chapters[currentChapterIndex - 1]
          commit('setCurrentChapter', prevChapter.id)
          commit('setCurrentLesson', prevChapter.lessons[prevChapter.lessons.length - 1].id)
        }
      }
    }
  },
  getters: {
    currentChapter: state => {
      return state.courseData.chapters.find(
        ch => ch.id === state.learningProgress.currentChapter
      ) || null
    },
    currentLesson: state => {
      const currentChapter = state.courseData.chapters.find(
        ch => ch.id === state.learningProgress.currentChapter
      )
      if (!currentChapter) return null
      return currentChapter.lessons.find(
        lesson => lesson.id === state.learningProgress.currentLesson
      ) || null
    },
    progressPercentage: state => {
      const totalLessons = state.courseData.chapters.reduce(
        (total, chapter) => total + chapter.lessons.length, 0
      )
      return totalLessons > 0 ? Math.round((state.learningProgress.completedLessons.length / totalLessons) * 100) : 0
    },
    // 新增的getters用于课程类型数据
    currentCourseData: state => {
      console.log('currentCourseData getter called:', {
        currentCourseType: state.currentCourseType,
        courseTypes: state.courseData.courseTypes,
        'courseTypes[currentCourseType]': state.courseData.courseTypes[state.currentCourseType]
      });
      if (!state.currentCourseType || !state.courseData.courseTypes[state.currentCourseType]) {
        console.log('currentCourseData returning null');
        return null
      }
      console.log('currentCourseData returning:', state.courseData.courseTypes[state.currentCourseType]);
      return state.courseData.courseTypes[state.currentCourseType]
    },
    currentCourseVideos: (_, getters) => {
      const courseData = getters.currentCourseData;
      return courseData && courseData.videos && Array.isArray(courseData.videos) ? courseData.videos : []
    },
    currentCourseTest: (_, getters) => {
      return getters.currentCourseData ? getters.currentCourseData.test : null
    },
    currentVideo: (state, getters) => {
      if (!state.currentVideoId || !getters.currentCourseVideos || !Array.isArray(getters.currentCourseVideos)) {
        return null
      }
      return getters.currentCourseVideos.find(video => video.id === parseInt(state.currentVideoId))
    },
    // 移除isVideoCompleted方法，现在使用API返回的unlocked字段
    // 检查视频是否可以观看（使用API返回的unlocked字段）
    isVideoUnlocked: (state, getters) => (courseType, videoId) => {
      // 从store中的课程数据获取视频解锁状态
      const courseData = state.courseData.courseTypes[courseType];
      if (!courseData || !courseData.videos || !Array.isArray(courseData.videos)) {
        return false;
      }
      const video = courseData.videos.find(v => v.id === videoId);
      return video ? video.unlocked : false;
    },
    // 获取课程的视频完成进度
    getCourseProgress: (state, getters) => (courseType) => {
      if (!state.courseData.courseTypes[courseType]) {
        return { completed: 0, total: 0, percentage: 0 }
      }

      const videos = state.courseData.courseTypes[courseType].videos
      if (!videos || !Array.isArray(videos)) {
        return { completed: 0, total: 0, percentage: 0 }
      }

      const unlocked = videos.filter(video => video.unlocked).length

      return {
        completed: unlocked,
        total: videos.length,
        percentage: Math.round((unlocked / videos.length) * 100)
      }
    }
  }
}) 